import React, { useMemo } from 'react';
import Button from '../Button';
import Unread from '../Unread';
import ResourceDialog from './ResourceDialog';
import Breadcrumb, { FileGlyph, FolderGlyph } from './Breadcrumb';
import { useFiles } from '../../data/useFiles';
import useResourceBrowser from '../../hooks/useResourceBrowser';
import { isLinkable, isNoteImage, resourceMessage } from '../../domain/resources';
import s from './resources.module.css';

// "Insert an Image from Online Resources" — the lesson-note editor's Insert
// Image (#531). Ours, not MMS's: MMS's note editor is Froala, storing HTML,
// and it has no library picture to insert; there is nothing of its to copy.
//
// The library's own browser, as the Link an Online Resource picker is — the
// breadcrumb from Home, folders to open — showing only the files a note draws
// as pictures (`is_note_image` over the upload rule, core/src/resources.rs)
// that are still stored (`is_linkable`). One click picks one picture and
// closes, because what happens next is text appearing at the cursor, and the
// teacher can see it and insert another.
//
// What a pick writes is up to the editor (`libraryImageMarkdown`): a reference
// by the row's id, never a link, so the note holds nothing that expires.
export default function ImagePicker({ open, onPick, onClose }) {
  const {
    files, folders, loaded, failed,
  } = useFiles();
  // Only the pictures, before the browser lists a folder: a folder of PDFs
  // reads as empty here, which is the truth about what can be inserted.
  const pictures = useMemo(() => files.filter((r) => isLinkable(r) && isNoteImage(r.contentType)), [files]);
  const browser = useResourceBrowser({ folders, files: pictures });
  const empty = !browser.folders.length && !browser.files.length;

  return (
    <ResourceDialog
      open={open}
      title={resourceMessage('image_picker_title')}
      onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>Cancel</Button>}
    >
      <Breadcrumb crumbs={browser.crumbs} onOpen={browser.open} />
      <div className={s.options}>
        {browser.folders.map((f) => (
          <button type="button" key={f.id} className={s.option} onClick={() => browser.open(f.id)}>
            <FolderGlyph />
            {f.name}
          </button>
        ))}
        {browser.files.map((r) => (
          <button
            type="button"
            key={r.id}
            className={s.option}
            aria-label={`${resourceMessage('image_picker_action')} ${r.name}`}
            onClick={() => { onPick(r); onClose(); }}
          >
            <FileGlyph />
            {r.name}
          </button>
        ))}
        {empty && !loaded ? <Unread failed={failed} /> : empty ? (
          <div className={s.empty}>
            {browser.folderId == null && files.length === 0
              ? resourceMessage('library_empty')
              : resourceMessage('no_images')}
          </div>
        ) : null}
      </div>
    </ResourceDialog>
  );
}
